import { KeyValue } from '@angular/common';
import { Component, NgZone, OnDestroy, OnInit, QueryList, ViewChildren } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import { AuthService } from '../../auth/auth.service';
import { UserDto } from '../../auth/user.dto';
import { ConfirmationComponent } from '../../shared/confirmation/confirmation.component';
import { BeneficiaryFilter } from '../../shared/filter/beneficiaryFilter';
import { DisplayBeneficiaryDto } from '../../shared/models/display-beneficiary.dto';
import { DisplayLabel } from '../../shared/models/displayLabel';
import { FolderStatus, labelsByFolderStatus } from '../../shared/models/enums/folderStatus.enum';
import { SupportTypes } from '../../shared/models/enums/support-type.enum';
import { Pagination } from '../../shared/pagination/pagination';
import { PaginationOptions } from '../../shared/pagination/pagination-options';
import { BeneficiariesService } from '../../shared/services/beneficiaries.service';
import { LocalStorageService } from '../../shared/services/local-storage.service';
import { StringUtils } from '../../shared/services/stringUtils.service';
import { StructureService } from '../../shared/services/structure.service';
import { SupportTypeService } from '../../shared/services/supportType.service';
import { TitleService } from '../../shared/services/title.service';
import { UserService } from '../../shared/services/user.service';
import { SortDirection, SortEvent } from '../../shared/sortable/sort-event';
import { SortableDirective } from '../../shared/sortable/sortable.directive';
import { ToastService } from '../../shared/toast/toast.service';
import { Utils } from '../../shared/utils';
import { DateValidators } from '../../shared/validators/date.validators';
import { InputValidators } from '../../shared/validators/input.validators';

@Component({
  selector: 'app-beneficiary-list',
  templateUrl: './beneficiary-list.component.html',
  styleUrls: ['./beneficiary-list.component.scss'],
})
export class BeneficiaryListComponent implements OnInit, OnDestroy {
  stringAscOrder = StringUtils.stringAscOrder;

  private static readonly resetBeneficiaryList: Pagination<DisplayBeneficiaryDto> = new Pagination<DisplayBeneficiaryDto>(
    [],
    0,
    0,
    1,
    new PaginationOptions(1, 25),
  );

  public beneficiaryFormGroup: UntypedFormGroup | undefined;
  public beneficiaryFilter: BeneficiaryFilter = new BeneficiaryFilter();
  public searchPerformed: boolean = false;
  public advancedFilters: boolean = false;
  public supportTypes: DisplayLabel[] = [];
  public structures: DisplayLabel[] = [];

  public readonly folderStatus: Map<FolderStatus, string> = labelsByFolderStatus;
  public readonly StringUtils: StringUtils = StringUtils;
  private user: UserDto = undefined;
  public userList: UserDto[] = [];

  private readonly destroy$: Subject<void> = new Subject();
  public isHistoMode: boolean = false;

  @ViewChildren(SortableDirective) headers: QueryList<SortableDirective>;
  public beneficiaries: Pagination<DisplayBeneficiaryDto> = BeneficiaryListComponent.resetBeneficiaryList;

  constructor(
    private readonly beneficiariesService: BeneficiariesService,
    private readonly titleService: TitleService,
    private readonly formBuilder: UntypedFormBuilder,
    private readonly supportTypeService: SupportTypeService,
    private readonly structureService: StructureService,
    private readonly authService: AuthService,
    private readonly userService: UserService,
    private readonly localStorageService: LocalStorageService,
    private readonly modalService: NgbModal,
    private readonly toastService: ToastService,
    private readonly router: Router,
    private readonly zone: NgZone,
  ) {}

  public async ngOnInit(): Promise<void> {
    this.titleService.setTitle('Bénéficiaires');
    this.supportTypes = (await this.supportTypeService.findAll()).filter((value) => value.id !== SupportTypes.HORS_ACCOMPAGNEMENT);
    this.user = await this.authService.getUser();

    if (this.user && this.user.groups && this.user.groups.includes('STRUCTURE') && this.user.structureId) {
      this.userList = await this.userService.findAll(this.user.structureId);
    }

    if (
      this.user &&
      this.user.groups &&
      this.user.groups.some((group: string) => ['ADMIN', 'DIRECTION', 'DIRECTION+', 'TERRITORY'].includes(group))
    ) {
      this.structures = await this.structureService.findAllInBeneficiarySupportArea();
    }
    this.beneficiaryFormGroup = this.formBuilder.group({
      nameOrCAF: ['', [InputValidators.alphabeticalFormatValidator]],
      firstName: ['', [InputValidators.alphabeticalWildcardFormatValidator]],
      birthDate: ['', [DateValidators.dateFormatValidator]],
      supportType: [''],
      structure: [''],
      referent: [''],
      status: [''],
      searchFromCommunity: [false],
      searchRetroactive: [false],
    });

    this.beneficiariesService.beneficiaryDeleted$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.onSubmit();
    });

    await this.preLoad();
  }

  public reinitFilters(): void {
    this.resetFilters();
    this.beneficiaryFormGroup.reset({
      nameOrCAF: '',
      firstName: '',
      birthDate: '',
      supportType: '',
      structure: '',
      referent: '',
      status: '',
      searchFromCommunity: false,
      searchRetroactive: false,
    });
    this.advancedFilters = false;
    this.localStorageService.remove('beneficiary_last_search');
  }

  public async onSubmit(options?: PaginationOptions): Promise<void> {
    if (this.beneficiaryFormGroup.valid) {
      this.resetList();
      await this.applyFilters(options || this.beneficiaries.options);
    } else {
      this.resetFilters();
    }
  }

  public async onSort(event: SortEvent): Promise<void> {
    const column: string = event.column;
    const direction: SortDirection = event.direction;
    this.headers.forEach((header: SortableDirective) => {
      if (header.sortable !== column) {
        header.direction = '';
      }
    });
    this.applyFilters(this.beneficiaries.options.withSort(column, direction === 'desc'));
  }

  public async changePage(pageNb?: number): Promise<void> {
    if (pageNb) {
      this.applyFilters(this.beneficiaries.options.withPage(pageNb));
    }
  }

  public async changeLimit(event: any): Promise<void> {
    if (event && event.target && event.target.value) {
      const limit: number = event.target.value;
      this.applyFilters(this.beneficiaries.options.withLimit(limit));
    }
  }

  public valueAscOrder(a: KeyValue<number, DisplayLabel>, b: KeyValue<number, DisplayLabel>): number {
    return a.value.label.localeCompare(b.value.label, undefined, { sensitivity: 'base' });
  }

  public async retrieveLastBeneficiaries(): Promise<void> {
    this.isHistoMode = true;
    const items: any[] = this.localStorageService.get<{ beneficiary: DisplayBeneficiaryDto; accessMode: string }[]>('last_beneficiaries');
    if ((items || []).length > 0) {
      this.resetFilters();
      this.searchPerformed = true;
      this.beneficiaryFormGroup.reset();
      this.beneficiaries = {
        items: items.map((item: { beneficiary: DisplayBeneficiaryDto; accessMode: string }) => item.beneficiary),
        itemCount: items.length,
        totalItems: items.length,
        options: {
          page: 1,
          limit: 10,
          desc: false,
        } as PaginationOptions,
      } as Pagination<DisplayBeneficiaryDto>;
    }
  }

  private getFilterValues(): BeneficiaryFilter {
    const beneficiaryFilter: BeneficiaryFilter = new BeneficiaryFilter();
    beneficiaryFilter.searchFromCommunity = this.beneficiaryFormGroup.controls.searchFromCommunity.value;
    beneficiaryFilter.searchRetroactive = this.beneficiaryFormGroup.controls.searchRetroactive.value;

    if (Utils.isNotEmpty(this.beneficiaryFormGroup.controls.nameOrCAF.value)) {
      beneficiaryFilter.nameOrCAF = this.beneficiaryFormGroup.controls.nameOrCAF.value;
    }

    if (this.advancedFilters) {
      if (Utils.isNotEmpty(this.beneficiaryFormGroup.controls.firstName.value)) {
        beneficiaryFilter.firstName = this.beneficiaryFormGroup.controls.firstName.value;
      }
      if (Utils.isNotEmpty(this.beneficiaryFormGroup.controls.birthDate.value)) {
        beneficiaryFilter.birthDate = this.beneficiaryFormGroup.controls.birthDate.value;
      }
      if (Utils.isNotEmpty(this.beneficiaryFormGroup.controls.supportType.value)) {
        beneficiaryFilter.supportTypeId = this.beneficiaryFormGroup.controls.supportType.value;
      }
      if (Utils.isNotEmpty(this.beneficiaryFormGroup.controls.structure.value)) {
        beneficiaryFilter.structureId = this.beneficiaryFormGroup.controls.structure.value;
      }
      if (Utils.isNotEmpty(this.beneficiaryFormGroup.controls.status.value)) {
        beneficiaryFilter.status = this.beneficiaryFormGroup.controls.status.value;
      }
      if (Utils.isNotEmpty(this.beneficiaryFormGroup.controls.referent.value) && this.user && this.user.structureId) {
        beneficiaryFilter.referentId = this.beneficiaryFormGroup.controls.referent.value;
      }
    }

    return beneficiaryFilter;
  }

  private async applyFilters(options: PaginationOptions): Promise<void> {
    const filterValues: BeneficiaryFilter = this.getFilterValues();
    if (!filterValues.isEmpty()) {
      this.isHistoMode = false;
      this.searchPerformed = true;
      this.beneficiaryFilter = filterValues;
      this.beneficiaries = await this.beneficiariesService.findByFilter(this.beneficiaryFilter, options);
      this.localStorageService.set('beneficiary_last_search', {
        email: this.user.email,
        options: this.beneficiaries.options,
        filter: this.beneficiaryFormGroup.value,
      });
    } else {
      this.resetFilters();
    }
  }

  private resetFilters(): void {
    this.beneficiaries = BeneficiaryListComponent.resetBeneficiaryList;
    this.beneficiaryFilter = new BeneficiaryFilter();
    this.searchPerformed = false;
  }

  private resetList(): void {
    this.beneficiaries = BeneficiaryListComponent.resetBeneficiaryList;
  }

  public async deleteBeneficiary(beneficiaryId: number): Promise<void> {
    const confirm: boolean = await this.openConfirmationModal();
    if (confirm) {
      try {
        await this.beneficiariesService.deleteBeneficiary(beneficiaryId);
        this.toastService.show('Le bénéficiaire a été supprimé avec succès.', 'success');
      } catch (e) {
        if (e.status === 453) {
          this.toastService.show('Le dossier du bénéficiaire ne peut être supprimé car il contient un parcours RSJ.', 'danger');
        } else {
          this.toastService.show('Une erreur serveur est survenue, merci de réessayer plus tard.', 'danger');
        }
      }
    }
  }

  private async openConfirmationModal(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return modalComponent.confirm('Êtes-vous sûr(e) de vouloir supprimer ce bénéficiaire ?');
  }

  public handleSearchModeChanged(value: string): void {
    switch (value) {
      case 'retroactive':
        this.beneficiaryFormGroup.controls.searchRetroactive.value
          ? this.beneficiariesService.accessMode.setValue('retroactive')
          : this.beneficiariesService.accessMode.setValue('default');
        if (this.beneficiaryFormGroup.controls.searchFromCommunity.value) {
          this.beneficiaryFormGroup.controls.searchFromCommunity.setValue(false);
        }
        break;
      case 'participant':
        this.beneficiaryFormGroup.controls.searchFromCommunity.value
          ? this.beneficiariesService.accessMode.setValue('participant')
          : this.beneficiariesService.accessMode.setValue('default');
        if (this.beneficiaryFormGroup.controls.searchRetroactive.value) {
          this.beneficiaryFormGroup.controls.searchRetroactive.setValue(false);
        }
        break;
      default:
        this.beneficiariesService.accessMode.setValue('default');
        this.beneficiaryFormGroup.controls.searchRetroactive.setValue(false);
        this.beneficiaryFormGroup.controls.searchFromCommunity.setValue(false);
        break;
    }
  }

  public navigateToBeneficiary(beneficiary: DisplayBeneficiaryDto, destination: string[]): void {
    const lastBeneficiaries: any[] =
      this.localStorageService.get<{ beneficiary: DisplayBeneficiaryDto; accessMode: string }[]>('last_beneficiaries') || [];
    const index: number = lastBeneficiaries.findIndex(
      (item: { beneficiary: DisplayBeneficiaryDto; accessMode: string }) => item.beneficiary.id === beneficiary.id,
    );
    if (!this.isHistoMode) {
      index > -1 && lastBeneficiaries.splice(index, 1);

      lastBeneficiaries.unshift({
        beneficiary,
        accessMode: this.beneficiaryFormGroup.controls.searchFromCommunity.value
          ? 'participant'
          : this.beneficiaryFormGroup.controls.searchRetroactive.value
            ? 'retroactive'
            : 'default',
      });

      if (lastBeneficiaries.length > 10) {
        lastBeneficiaries.pop();
      }
      this.localStorageService.set('last_beneficiaries', lastBeneficiaries);
    } else {
      const item: any = lastBeneficiaries.find(
        (item: { beneficiary: DisplayBeneficiaryDto; accessMode: string }) => item.beneficiary.id === beneficiary.id,
      );
      this.beneficiariesService.accessMode.setValue(item.accessMode);
    }
    this.router.navigate(['/beneficiaries', beneficiary.id, ...destination]);
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private async preLoad(): Promise<void> {
    const last_search: { email: string; options: PaginationOptions; filter: any } = this.localStorageService.get<{
      email: string;
      options: PaginationOptions;
      filter: any;
    }>('beneficiary_last_search');

    if (last_search && last_search.email === this.user?.email) {
      this.beneficiaryFormGroup.setValue(last_search.filter);
      this.setAdvancedFilters();
      const options: PaginationOptions = PaginationOptions.fromJson(last_search.options);
      await this.onSubmit(options);
      this.setSortHeaders(options);
    } else {
      this.localStorageService.remove('beneficiary_last_search');
    }
  }

  private setAdvancedFilters(): void {
    this.advancedFilters =
      Utils.isNotEmpty(this.beneficiaryFormGroup.controls.firstName.value) ||
      Utils.isNotEmpty(this.beneficiaryFormGroup.controls.birthDate.value) ||
      Utils.isNotEmpty(this.beneficiaryFormGroup.controls.supportType.value) ||
      Utils.isNotEmpty(this.beneficiaryFormGroup.controls.structure.value) ||
      Utils.isNotEmpty(this.beneficiaryFormGroup.controls.status.value) ||
      !!(Utils.isNotEmpty(this.beneficiaryFormGroup.controls.referent.value) && this.user && this.user.structureId);
  }

  private setSortHeaders(options: PaginationOptions): void {
    if (options?.sort) {
      this.zone.onStable.pipe(takeUntil(this.destroy$), first()).subscribe(() => {
        this.headers
          .filter((item: SortableDirective) => item.sortable === options.sort)
          .forEach((item: SortableDirective) => (item.direction = options.desc ? 'desc' : 'asc'));
      });
    }
  }
}
